/**
 * Collect Debt Records — subsection 1 (/sframe-199-s1)
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import DebtReviewSectionShell, { type ChecklistItemDef } from './DebtReviewSectionShell';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';

const CHECKLIST_ITEMS: ChecklistItemDef[] = [
  {
    id: 'collect-1',
    title: 'Search Digital Footprint',
    description: 'Review email inboxes, online accounts, and digital statements for creditor communications.',
  },
  {
    id: 'collect-2',
    title: 'Request Credit Report',
    description: 'Obtain credit reports from all three bureaus: Equifax, Experian, TransUnion.',
  },
  {
    id: 'collect-3',
    title: 'Organize Files',
    description: 'Sort and label all debt-related documents by creditor.',
  },
];

type DebtRecord = {
  id: string;
  creditorName: string;
  type: string;
  balance: string;
  notes: string;
  fileName?: string;
  fileDataUrl?: string;
};

const FRAME_ID = 'sframe-199-s1';

function newId(): string {
  return `dr-${crypto.randomUUID()}`;
}

function DebtRecordsManager(props: {
  disabled: boolean;
  records: DebtRecord[];
  onChange(next: DebtRecord[]): void;
}) {
  const { disabled, records, onChange } = props;
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DebtRecord | null>(null);

  const startCreate = () => {
    setDraft({
      id: newId(),
      creditorName: '',
      type: '',
      balance: '',
      notes: '',
    });
    setShowForm(true);
  };

  const startEdit = (record: DebtRecord) => {
    setDraft(record);
    setShowForm(true);
  };

  const cancelForm = () => {
    setDraft(null);
    setShowForm(false);
  };

  const saveDraft = () => {
    if (!draft?.creditorName.trim()) return;
    const idx = records.findIndex((r) => r.id === draft.id);
    const next =
      idx === -1 ? [...records, draft] : records.map((r) => (r.id === draft.id ? { ...draft } : r));
    onChange(next);
    cancelForm();
  };

  const deleteRecord = (id: string) => {
    onChange(records.filter((r) => r.id !== id));
  };

  const onAttachFile = (file: File | undefined) => {
    if (!file || !draft) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : undefined;
      setDraft((d) =>
        d ? { ...d, fileName: file.name, fileDataUrl: dataUrl || undefined } : d
      );
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between gap-4 mb-3">
        <h2 className="text-lg font-semibold text-stone-900">Debt document management</h2>
        <button
          type="button"
          disabled={disabled}
          onClick={startCreate}
          className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 hover:bg-stone-50 disabled:opacity-50 transition-colors"
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          Add Debt Record
        </button>
      </div>

      <div className="space-y-3">
        {records.length === 0 ? (
          <div className="rounded-xl border border-dashed border-stone-200 bg-stone-50 p-5 text-sm text-stone-600">
            No debt records yet — add creditor entries and attach statements as you locate them.
          </div>
        ) : (
          records.map((r) => (
            <div key={r.id} className="rounded-xl border border-stone-200 bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-stone-900 truncate">{r.creditorName || '(Unnamed creditor)'}</p>
                  <p className="text-sm text-stone-600 mt-1">
                    <span className="font-medium text-stone-700">Type:</span> {r.type || '—'}{' '}
                    <span className="mx-2 text-stone-300">|</span>{' '}
                    <span className="font-medium text-stone-700">Balance:</span> {r.balance || '—'}
                  </p>
                  {r.notes ? <p className="text-sm text-stone-600 mt-2 whitespace-pre-wrap">{r.notes}</p> : null}
                  {r.fileName ? (
                    <p className="text-sm text-stone-600 mt-2">
                      <span className="font-medium text-stone-700">Attached:</span> {r.fileName}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => startEdit(r)}
                    className="rounded-lg border border-stone-200 bg-white p-2 text-stone-700 hover:bg-stone-50 disabled:opacity-50 transition-colors"
                    aria-label="Edit debt record"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => deleteRecord(r.id)}
                    className="rounded-lg border border-stone-200 bg-white p-2 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors"
                    aria-label="Delete debt record"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showForm && draft ? (
        <div className="mt-4 rounded-xl border border-stone-200 bg-white p-5">
          <p className="text-sm font-medium text-stone-900 mb-4">New / edit debt record</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-stone-700">
              Creditor Name
              <input
                className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
                disabled={disabled}
                value={draft.creditorName}
                onChange={(e) => setDraft({ ...draft, creditorName: e.target.value })}
                placeholder="e.g., Example Bank"
              />
            </label>
            <label className="text-sm text-stone-700">
              Type
              <input
                className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
                disabled={disabled}
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                placeholder="e.g., Credit card"
              />
            </label>
            <label className="text-sm text-stone-700">
              Balance
              <input
                className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
                disabled={disabled}
                value={draft.balance}
                onChange={(e) => setDraft({ ...draft, balance: e.target.value })}
                placeholder="e.g., $12,345.67"
              />
            </label>
            <label className="text-sm text-stone-700">
              Notes
              <textarea
                className="mt-1 w-full min-h-[92px] rounded-lg border border-stone-200 px-3 py-2 text-sm"
                disabled={disabled}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Anything important about this creditor / claim..."
              />
            </label>
            <label className="text-sm text-stone-700 sm:col-span-2">
              File Upload
              <input
                className="mt-1 block w-full text-sm"
                disabled={disabled}
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => onAttachFile(e.target.files?.[0])}
              />
            </label>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button
              type="button"
              disabled={disabled}
              onClick={saveDraft}
              className="rounded-lg bg-stone-900 text-white px-4 py-2 text-sm hover:bg-stone-800 disabled:opacity-50 transition-colors"
            >
              Save record
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={cancelForm}
              className="rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm text-stone-800 hover:bg-stone-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function ReviewOrganize_CollectDebts() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, isLoading } = useSettlementSession(estateId ?? null, {
    readOnly: isArchived,
  });

  const [records, setRecords] = useState<DebtRecord[]>([]);

  const reloadRecords = useCallback(async () => {
    if (!estateId) return;
    const framed = await loadFrameData(FRAME_ID);
    const raw = framed?.data?.debtRecords;
    if (Array.isArray(raw)) {
      setRecords(raw as DebtRecord[]);
    } else {
      setRecords([]);
    }
  }, [estateId, loadFrameData]);

  useEffect(() => {
    void reloadRecords();
  }, [reloadRecords]);

  const persistRecords = async (next: DebtRecord[]) => {
    if (!estateId || isArchived) return;
    const framed = await loadFrameData(FRAME_ID);
    const prev =
      typeof framed?.data === 'object' && framed?.data ? (framed.data as Record<string, unknown>) : {};
    await saveFrameData(FRAME_ID, { ...prev, debtRecords: next });
    setRecords(next);
  };

  const disabled = isArchived || isLoading;

  return (
    <DebtReviewSectionShell
      estateId={estateId}
      title="Collect Debt Records"
      sectionIndex={1}
      checklistFrameId={FRAME_ID}
      hubSectionKey="s1"
      items={CHECKLIST_ITEMS}
    >
      <DebtRecordsManager
        disabled={disabled}
        records={records}
        onChange={(n) => void persistRecords(n)}
      />
    </DebtReviewSectionShell>
  );
}
